import {
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Input,
  InputLabel,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import NavBar from "../../components/navbar";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../hooks";
import { IMaskInput } from "react-imask";
import { LoadPositionList } from "../../../../domain/usecases/load-position-list";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import { Position } from "../../../../domain/models/position-model";
import { ReceiptCheckItem } from "../../../../domain/models/receipt-check";
import { UpdateReceiptCheckItem } from "../../../../domain/usecases/update-receipt-check-item";
import Inventory from "@mui/icons-material/Inventory";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00.00.00.00"
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

type Props = {
  loadPosition: LoadPositionList;
  loadProductList: LoadProductListByManyCodes;
  updateReceiptCheckItem: UpdateReceiptCheckItem;
};

const AdjustBatches = ({
  loadPosition,
  loadProductList,
  updateReceiptCheckItem,
}: Props) => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [newBatch, setNewBatch] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState<ReceiptCheckItem | null>(null);
  const navigate = useNavigate();

  const handleError = useErrorHandler((e) => {
    alert(e.message);
  });

  const handleClick = async () => {
    if (!code) return;
    const warehouseString = localStorage.getItem("currentWarehouse");
    const warehouse = warehouseString
      ? (JSON.parse(warehouseString) as {
          id: number;
          name: string;
        } | null)
      : undefined;
    const [street, building, level, apartment] = code.split(".");
    const params: any = {
      warehouseId: warehouse!.id,
      filterOptions: {
        includeInventory: true,
      },
    };
    if (street) {
      params.filterOptions.street = Number(street);
    }
    if (building) {
      params.filterOptions.building = Number(building);
    }
    if (level) {
      params.filterOptions.level = Number(level);
    }
    if (apartment) {
      params.filterOptions.apartment = Number(apartment);
    }

    setIsLoading(true);
    try {
      const res = await loadPosition.load(params);

      if (res) {
        const codes: string[] = [];
        res.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            codes.push(inventory!.item!.code);
          });
        });
        if (codes.length === 0) return setPositions(res);
        const products = await loadProductList.load(codes);
        res.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            const match = products.find(
              (product) => product.eanCode === inventory.item?.code
            );
            inventory.item!.description = match?.description;
            inventory.item!.packageAmount = match?.packageAmount;
            inventory.item!.reference = match?.reference;
            inventory.item!.unit = match?.unit!;
          });
        });
        setPositions(res);
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeBatch = async (id: number, batch: string) => {
    setIsLoading(true);
    try {
      await updateReceiptCheckItem.update(id, batch);
      setPositions(() => {
        positions.forEach((position) => {
          const item = position.inventories?.find(
            (inventory) => inventory.item?.id === id
          );
          if (item) {
            item.item!.batch = batch;
          }
        });
        return positions;
      });
      setCurrentItem(null);
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavBar
        title="Ajustar Lote"
        onBack={() => {
          navigate("/adjustments");
        }}
      />
      <Dialog
        open={!!currentItem}
        onClose={() => {
          setCurrentItem(null);
        }}
      >
        <DialogTitle>Alterar lote do produto?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao alterar o lote desse produto, toda quantidade do item conferida
            no recebimento será afetada, inclusive quantidades que podem estar
            em outras posições.
          </DialogContentText>
          <Stack spacing={2} my={2}>
            <Stack>
              <div style={{ fontWeight: "bold" }}>Descrição</div>
              <div>{currentItem?.description}</div>
            </Stack>
            <Stack>
              <div style={{ fontWeight: "bold" }}>Lote</div>
              <div>{currentItem?.batch}</div>
            </Stack>
          </Stack>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Novo lote"
            fullWidth
            variant="outlined"
            value={newBatch}
            onChange={(e) => {
              setNewBatch(e.target.value);
            }}
          />
        </DialogContent>
        {isLoading ? (
          <DialogActions
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div>
              <CircularProgress />
            </div>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button
              onClick={() => {
                setCurrentItem(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (currentItem?.id) {
                  handleChangeBatch(currentItem?.id, newBatch);
                }
              }}
            >
              Confirmar
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack direction="row" spacing={4}>
          <FormControl>
            <InputLabel htmlFor="formatted-text-mask-input">Posição</InputLabel>
            <Input
              value={code}
              onChange={(e: any) => {
                setCode(e.target.value);
              }}
              name="textmask"
              id="formatted-text-mask-input"
              inputComponent={TextMaskCustom as any}
            />
          </FormControl>
        </Stack>
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={handleClick}
        >
          Pesquisar
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        {positions.map((position) => {
          return position.inventories?.map((inventory) => {
            return (
              <Paper
                key={inventory.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  padding: "20px",
                  marginTop: "30px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "space-between",
                    width: "100%",
                  }}
                >
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Código EAN</div>
                    <div>{inventory.item?.code}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Descrição</div>
                    <div>{inventory.item?.description}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Quantidade</div>
                    <div>{`${(
                      inventory.amount / (inventory.item?.packageAmount || 1)
                    ).toFixed(2)} (${inventory.amount} ${
                      inventory.item?.unit
                    })`}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Referência</div>
                    <div>{inventory.item?.reference}</div>
                  </Stack>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >
                  <Stack direction="row" spacing={3}>
                    <div style={{ fontWeight: "bold" }}>Lote</div>
                    <div>{inventory.item?.batch}</div>
                  </Stack>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentItem(inventory.item || null);
                    }}
                  >
                    Alterar Lote
                  </Button>
                </div>
              </Paper>
            );
          });
        })}
      </Container>
    </>
  );
};

export default AdjustBatches;
